import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import InfoIcon from './InfoIcon';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { useValidation } from './ValidationContext';

const validationRules = {
  required: (value) => {
    // Ensure value is a string before calling .trim()
    const stringValue = (value !== undefined) ? String(value) : '';
    return {
      isValid: stringValue.trim() !== '',
      message: 'Bitte ausfüllen.'
    };
  },
  optional: (value) => {
    // Ensure value is a string before calling .trim()
    const stringValue = value ? String(value) : '';
    return {
      isValid: stringValue.trim() !== 'undefined',
      message: 'Bitte ausfüllen.'
    };
  },
};

const CustomSelect = forwardRef(({ rules, label, style, children, formData, onChange, sendChat, selected, ...props }, ref) => {
  const [selectedValue, setSelectedValue] = useState((formData && formData[props.name]) || "");
  const [errors, setErrors] = useState([]);
  const [validationContextError, setValidationContextError] = useState([]);
  const { registerValidator } = useValidation();

  useEffect(() => {
    //console.log("CustomSelect ref:", ref);
  }, [ref]);

  useEffect(() => {
    if (formData && props.name && formData.hasOwnProperty(props.name)) {
      setSelectedValue(prev => {
        // Nur setzen, wenn initial leer ist (z. B. beim ersten Render)
        return prev === "" ? String(formData[props.name]) : prev;
      });
    }
  }, [formData, props.name]);

  // Assign validateField method to ref so that it can be accessed in parent
  useImperativeHandle(ref, () => ({
    validateField
  }));

  const validateField = (new_value = selectedValue) => {
    //console.log("validateField in CustomSelect");
    //console.log('new_value: ', new_value);
    //console.log('selectedValue: ', selectedValue);
    //console.log('label: ', label);

    setErrors([]); // Reset errors

    const newErrors = rules.map(rule => {
        const { ruleKey, ruleParam, customRule } = rule;
        let validationRule = customRule;
        if (!validationRule && ruleKey) {
            validationRule = validationRules[ruleKey];
            if (typeof ruleParam !== 'undefined') {
                validationRule = validationRule(ruleParam);
            }
        }

        if (validationRule) {
            //console.log("VALUE BEFORE CHECK: " + new_value);
            const validationResponse = validationRule(new_value);
            if (!validationResponse.isValid) {
                return validationResponse.message;
            }
        }
        return null;
    }).filter(error => error !== null);

    setErrors(newErrors);
    //console.log('newErrors: ', newErrors);
    return newErrors.length === 0;
  };

  useEffect(() => {
      if (!rules || rules.length === 0) {
          throw new Error("CustomSelect requires 'rules' prop with at least one rule.");
      }
  }, [rules, selectedValue]);

  useEffect(() => {
    //console.log('validator for field: ', props.name);
    registerValidator(validateField);
  }, []);

  useEffect(() => {
    //console.log(`[useEffect] selectedValue changed:`, selectedValue);
  }, [selectedValue]);

  const handleChange = (event) => {
    setSelectedValue(event.target.value);
    if (typeof onChange === 'function') {
      onChange(event);
    }
  };

  // Trigger validateField when selectedValue changes
  useEffect(() => {
      if (selectedValue !== '') {
        validateField();
      }
  }, [selectedValue]);

  const defaultSelectedValue = formData ? formData[props.name] : "";

  return (
    <>
    <div className='inputField'>
      <div className="form-floating mb-3">
        <select {...props} ref={ref}
            className="form-control"
            value={selectedValue || defaultSelectedValue} // Controlled component
            onChange={handleChange}
            placeholder={label}
            >
            <option value="" disabled selected={!selected && "selected"}>
              Bitte auswählen
            </option>
            {children.map((child, index) => (
              <option
                key={index}
                value={child.props.value}
                selected={child.props.value == selected && 'selected' || ''}
                {...(formData && index == formData[props.name]) && 'selected' || ''}
              >
                {child.props.children}
              </option>
            ))}
            </select>
            <label htmlFor="customSelect">{label}</label>
            {errors.map((error, index) => (
                <div key={index} className="error-message" >{error}</div>
            ))}
        </div>
      </div>

      <div className='info-wrapper'>
        {sendChat &&
        <InfoIcon
          onClick={() => {
              //console.log("Mouse entered");
              if (formData && formData['info'] && formData['info'][props.name]) {
                  sendChat(formData['info'][props.name]); // Call sendChat with the correct data
              }

              // Scroll to the element with ID #info-icon-container
              const element = document.getElementById('hilfe');
              if (element) {
                  element.scrollIntoView({ behavior: 'smooth' }); // Smooth scroll to the element
              }
          }}
      >
          {formData && (formData['info'] && formData['info'][props.name]) || ""}
      </InfoIcon>}
      </div>
    </>
  );
});

export default CustomSelect;
